import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext} from "@angular/common/http";
import {Observable} from "rxjs";
import {NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {
  HospitalDto,
  ImportFileDto,
  MastercupHostCancelDto,
  MastercupHostDto,
  MastercupHostImportRowDto,
  MastercupHostRejectDto,
  MastercupHostRequestDto,
  MastercupHostRequestSearchParam,
  MastercupHostSearchParam,
  Page
} from "../../../generated-model/model";
import {environment} from "../../../environments/environment";
import {IgnoreNullHttpParams} from "../../shared/commons/Ignore-null-http-params";
import {PageRequest} from "../../app.constants";
import {IGNORE_400_ERROR} from "../../shared/commons/interceptor/http400-interceptor";
import {saveAs} from "file-saver";

@Injectable({
  providedIn: 'root'
})
export class MastercupHostService {

  private http = inject(HttpClient);
  private hostUrl = `${environment.serverUrl}/api/mastercup-host`;
  private requestUrl = `${environment.serverUrl}/api/mastercup-host-request`;
  /**
   * ค้นหาเครือข่ายสำหรับส่งคำร้อง — ผู้ใช้เขตเห็นเฉพาะเครือข่ายในเขตตัวเอง
   */
  readonly requestMastercupSearchUrl = `${this.requestUrl}/mastercups`;
  /**
   * จำนวนคำร้องที่รอพิจารณา
   */
  pendingRequestCount = 0;

  /**
   * ค้นหาหน่วยบริการที่เป็น HMAIN_HOST ได้ — ผู้ใช้เขตเห็นเฉพาะหน่วยบริการในเขตตัวเอง
   */
  searchHosts(term: string, pageRequest: PageRequest): Observable<Page<HospitalDto>> {
    const params = IgnoreNullHttpParams.fromObject({term: term || null, ...pageRequest}).toHttpParam();
    return this.http.get<Page<HospitalDto>>(`${this.hostUrl}/hosts`, {params});
  }

  /**
   * หน่วยบริการที่เป็น HMAIN_HOST ได้ทุกเขต (public API ใช้ในเมนูค้นหาเครือข่ายหน่วยบริการ)
   */
  searchAllHosts(term: string, pageRequest: PageRequest): Observable<Page<HospitalDto>> {
    const params = IgnoreNullHttpParams.fromObject({term: term || null, ...pageRequest}).toHttpParam();
    return this.http.get<Page<HospitalDto>>(`${environment.serverUrl}/public/api/mastercup/hmain-hosts`, {params});
  }

  search(param: MastercupHostSearchParam, pageRequest: PageRequest): Observable<Page<MastercupHostDto>> {
    const params = IgnoreNullHttpParams.fromObject({...param, ...pageRequest}).toHttpParam();
    return this.http.get<Page<MastercupHostDto>>(`${this.hostUrl}/search`, {params});
  }

  /**
   * ส่งออกผลการค้นหาทั้งหมด (ไม่แบ่งหน้า) เป็น .xlsx
   */
  exportXlsx(param: MastercupHostSearchParam): Observable<Blob> {
    const params = IgnoreNullHttpParams.fromObject({...param}).toHttpParam();
    return this.http.get(`${this.hostUrl}/export`, {params, responseType: 'blob'});
  }

  /**
   * ไฟล์ตัวอย่างสำหรับนำเข้า (.xlsx)
   */
  importTemplate(): Observable<Blob> {
    return this.http.get(`${this.hostUrl}/import/template`, {responseType: 'blob'});
  }

  /**
   * นำเข้าไฟล์ (.xlsx แนะนำ / .xls / .csv) — คืนทุกแถวพร้อมผลตรวจสอบ ยังไม่บันทึก
   */
  importFile(file: File): Observable<MastercupHostImportRowDto[]> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<MastercupHostImportRowDto[]>(`${this.hostUrl}/import`, formData);
  }

  /**
   * ตรวจสอบหลายรายการ ยังไม่บันทึก
   */
  validateAll(items: MastercupHostDto[]): Observable<MastercupHostImportRowDto[]> {
    return this.http.post<MastercupHostImportRowDto[]>(`${this.hostUrl}/validate`, items);
  }

  /**
   * เพิ่มหลายรายการ (บันทึกทั้งหมด หรือไม่บันทึกเลย)
   */
  createAll(items: MastercupHostDto[]): Observable<MastercupHostDto[]> {
    return this.http.post<MastercupHostDto[]>(`${this.hostUrl}/batch`, items);
  }

  cancel(id: number, dto: MastercupHostCancelDto): Observable<MastercupHostDto> {
    return this.http.put<MastercupHostDto>(`${this.hostUrl}/${id}/cancel`, dto);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.hostUrl}/${id}`);
  }

  searchRequest(param: MastercupHostRequestSearchParam, pageRequest: PageRequest): Observable<Page<MastercupHostRequestDto>> {
    const params = IgnoreNullHttpParams.fromObject({...param, ...pageRequest}).toHttpParam();
    return this.http.get<Page<MastercupHostRequestDto>>(`${this.requestUrl}/search`, {params});
  }

  /**
   * อัปโหลดไฟล์เอกสารแนบ (PDF / ZIP ที่ข้างในเป็น PDF) ไว้ใน temp ก่อนส่งคำร้อง — error แสดงที่หน้าจอเอง ไม่ใช้ toast กลาง
   */
  uploadRequestFile(file: File): Observable<ImportFileDto> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ImportFileDto>(`${this.requestUrl}/files`, formData,
      {context: new HttpContext().set(IGNORE_400_ERROR, true)});
  }

  /**
   * เปิดไฟล์เอกสารแนบของคำร้อง (ต้องส่ง token จึงโหลดเป็น blob ก่อน): PDF เปิดในแท็บใหม่ / ZIP ดาวน์โหลด
   */
  openRequestFile(requestId: number, file: ImportFileDto) {
    const url = `${this.requestUrl}/${requestId}/files/${file.id}`;
    if (isZipFile(file.originalFileName)) {
      this.http.get(url, {responseType: 'blob'}).subscribe(blob => saveAs(blob, file.originalFileName));
      return;
    }
    const win = window.open('', '_blank');
    this.http.get(url, {responseType: 'blob'})
      .subscribe({
        next: blob => {
          const objectUrl = URL.createObjectURL(blob);
          if (win) {
            win.location.href = objectUrl;
          } else {
            window.open(objectUrl, '_blank');
          }
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        },
        error: () => win?.close()
      });
  }

  createRequest(dto: MastercupHostRequestDto): Observable<MastercupHostRequestDto> {
    return this.http.post<MastercupHostRequestDto>(this.requestUrl, dto);
  }

  approveRequest(id: number): Observable<MastercupHostRequestDto> {
    return this.http.put<MastercupHostRequestDto>(`${this.requestUrl}/${id}/approve`, {});
  }

  rejectRequest(id: number, dto: MastercupHostRejectDto): Observable<MastercupHostRequestDto> {
    return this.http.put<MastercupHostRequestDto>(`${this.requestUrl}/${id}/reject`, dto);
  }

  /**
   * โหลดจำนวนคำร้องที่รอพิจารณาใหม่ (แสดงที่เมนูคำร้องขอเพิ่มเครือข่ายพิเศษ)
   */
  refreshPendingRequestCount() {
    this.http.get<number>(`${this.requestUrl}/count-pending`)
      .subscribe(count => this.pendingRequestCount = count);
  }
}

export function isZipFile(fileName: string): boolean {
  return !!fileName && fileName.toLowerCase().endsWith('.zip');
}

/**
 * แสดงหน่วยบริการเป็น hcode : htitle+hname (ไม่มี htitle ไม่ต้องแสดง, ไม่มีหน่วยบริการ = -)
 */
export function hospitalLabel(hospital: HospitalDto): string {
  if (!hospital?.hcode) {
    return '-';
  }
  return `${hospital.hcode} : ${hospital.title || ''}${hospital.hname || ''}`;
}

/**
 * วันที่น้อยที่สุดที่เลือกเป็นวันที่มีผลได้ = วันปัจจุบัน (ห้ามบันทึกย้อนหลัง)
 */
export function minStartDate(): NgbDateStruct {
  const now = new Date();
  return {year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate()};
}

const minEndDateCache = new Map<string, NgbDateStruct>();

/**
 * วันที่น้อยที่สุดที่เลือกเป็นวันที่สิ้นสุดได้ = วันปัจจุบัน หรือวันที่มีผล (yyyy-MM-dd) ถ้าวันที่มีผลมาทีหลัง
 * คืน object เดิมสำหรับค่าเดิม เพื่อใช้ bind ใน template ได้โดยไม่สร้าง object ใหม่ทุกรอบ change detection
 */
export function minEndDate(startDate?: string): NgbDateStruct {
  const today = minStartDate();
  const todayKey = today.year * 10000 + today.month * 100 + today.day;
  const [year, month, day] = (startDate || '').split('T')[0].split('-').map(Number);
  const startKey = year ? year * 10000 + month * 100 + day : 0;
  const key = Math.max(todayKey, startKey);
  if (!minEndDateCache.has(String(key))) {
    minEndDateCache.set(String(key), startKey > todayKey ? {year, month, day} : today);
  }
  return minEndDateCache.get(String(key));
}

export const MASTERCUP_HOST_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'มีผล',
  UPCOMING: 'ยังไม่ถึงวันที่มีผล',
  ENDED: 'สิ้นสุดแล้ว',
};

export const MASTERCUP_HOST_STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'bg-success',
  UPCOMING: 'bg-info text-dark',
  ENDED: 'bg-secondary',
};

export const MASTERCUP_HOST_REQUEST_STATUS_LABEL: Record<string, string> = {
  PENDING: 'รอพิจารณา',
  APPROVED: 'อนุมัติ',
  REJECTED: 'ปฏิเสธ',
};

export const MASTERCUP_HOST_REQUEST_STATUS_CLASS: Record<string, string> = {
  PENDING: 'bg-warning text-dark',
  APPROVED: 'bg-success',
  REJECTED: 'bg-danger',
};
